<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isVisible"
        class="modal-backdrop"
        @click.self="close"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-offer-title"
      >
        <div class="modal-panel">
          <header class="panel-header">
            <h2 id="create-offer-title">
              <i class="fas fa-plus-circle" aria-hidden="true"></i>
              Nouveau lot
            </h2>
            <button class="close-btn" @click="close" aria-label="Fermer">×</button>
          </header>

          <form class="panel-body" @submit.prevent="save">

            <!-- Source -->
            <div class="form-group">
              <label for="offer-source" class="form-label required">Source</label>
              <select id="offer-source" v-model="form.source" class="form-input">
                <option v-for="s in sources" :key="s.value" :value="s.value">
                  {{ s.label }}
                </option>
              </select>
            </div>

            <!-- Titre -->
            <div class="form-group">
              <label for="offer-title" class="form-label required">Titre</label>
              <input
                id="offer-title"
                v-model="form.title"
                type="text"
                class="form-input"
                :placeholder="titlePlaceholder"
                required
              >
            </div>

            <!-- URL (masqué pour manual) -->
            <div v-if="form.source !== 'manual'" class="form-group">
              <label for="offer-url" class="form-label">URL de l'annonce</label>
              <input
                id="offer-url"
                v-model="form.url"
                type="url"
                class="form-input"
                :placeholder="`URL ${currentSource.label}`"
              >
            </div>

            <!-- Référence (uniquement pour manual) -->
            <div v-if="form.source === 'manual'" class="form-group">
              <label for="offer-ref" class="form-label">Référence</label>
              <input
                id="offer-ref"
                v-model="form.reference"
                type="text"
                class="form-input"
                placeholder="Ex : Brocante Chatou, Ami Jean-Pierre, Stock…"
              >
            </div>

            <!-- Coût et date sur la même ligne -->
            <div class="form-row">
              <div class="form-group">
                <label for="offer-cost" class="form-label">Coût d'achat (€)</label>
                <input
                  id="offer-cost"
                  v-model.number="form.cost"
                  type="number"
                  min="0"
                  step="0.01"
                  class="form-input"
                  placeholder="0.00"
                >
              </div>
              <div class="form-group">
                <label for="offer-date" class="form-label">Date d'achat</label>
                <input
                  id="offer-date"
                  v-model="form.purchase_date"
                  type="date"
                  class="form-input"
                >
              </div>
            </div>

            <!-- Notes -->
            <div class="form-group">
              <label for="offer-notes" class="form-label">Notes</label>
              <textarea
                id="offer-notes"
                v-model="form.notes"
                class="form-input form-textarea"
                placeholder="Informations complémentaires…"
                rows="3"
              ></textarea>
            </div>

            <p v-if="errorMessage" class="create-error" role="alert">{{ errorMessage }}</p>

            <!-- Actions -->
            <div class="form-actions">
              <button type="button" class="btn btn-secondary" @click="close">
                Annuler
              </button>
              <button type="submit" class="btn btn-primary" :disabled="saving || !form.source || !form.title">
                <i v-if="saving" class="fas fa-spinner fa-spin" aria-hidden="true"></i>
                <i v-else class="fas fa-plus" aria-hidden="true"></i>
                {{ saving ? 'Création…' : 'Créer le lot' }}
              </button>
            </div>

          </form>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import axios from 'axios'
import config from '@/config/config.js'
import { SOURCES as SOURCE_INFO } from '../../sources'

// Sources a lot can be entered for (src/sources.js), in their usual order
const SOURCES = Object.entries(SOURCE_INFO).map(([value, source]) => ({ value, ...source }))

export default {
  name: 'OfferCreatePopup',
  props: {
    isVisible: { type: Boolean, default: false }
  },
  emits: ['close', 'created'],
  data() {
    return {
      sources: SOURCES,
      saving: false,
      errorMessage: '',
      form: this.emptyForm()
    }
  },
  computed: {
    currentSource() {
      return this.sources.find(s => s.value === this.form.source) || this.sources[0]
    },
    titlePlaceholder() {
      if (this.form.source === 'manual') return 'Ex : Lot brocante mars, Stock Gamecube…'
      const source = SOURCE_INFO[this.form.source]
      return source ? `Titre de l'annonce ${source.label}…` : 'Titre de l\'offre…'
    }
  },
  watch: {
    isVisible(val) {
      if (val) {
        this.form = this.emptyForm()
        this.errorMessage = ''
        document.addEventListener('keydown', this.handleKeydown)
      } else {
        document.removeEventListener('keydown', this.handleKeydown)
      }
    }
  },
  methods: {
    emptyForm() {
      return {
        source: 'manual',
        title: '',
        url: '',
        reference: '',
        cost: null,
        purchase_date: '',
        notes: ''
      }
    },
    close() {
      this.$emit('close')
    },
    handleKeydown(e) {
      if (e.key === 'Escape') this.close()
    },
    async save() {
      if (!this.form.source || !this.form.title) return

      this.saving = true
      this.errorMessage = ''
      try {
        const payload = {
          source: this.form.source,
          title: this.form.title,
          cost: this.form.cost || 0,
          notes: this.form.notes || '',
          purchase_date: this.form.purchase_date || null,
          url: this.form.source !== 'manual' ? this.form.url : null,
          reference: this.form.source === 'manual' ? this.form.reference : null
        }

        const response = await axios.post(`${config.apiUrl}/offers/manual`, payload)
        this.$emit('created', response.data)
        this.close()
      } catch (error) {
        console.error('Erreur création offre:', error)
        this.errorMessage = error.response?.status === 400
          ? 'Vérifiez le titre, la source et le coût saisis.'
          : 'Le lot n’a pas pu être créé. Réessayez dans un instant.'
      } finally {
        this.saving = false
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  }
}
</script>

<style scoped>
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .modal-panel, .modal-leave-to .modal-panel { transform: translateY(-30px) scale(0.97); }

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: calc(1rem + var(--safe-top)) 1rem calc(1rem + var(--safe-bottom));
  backdrop-filter: blur(2px);
}

.modal-panel {
  background: white;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  flex-shrink: 0;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: #2c3e50;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.panel-header h2 i { color: #3498db; }

.close-btn {
  background: none;
  border: none;
  font-size: 1.6rem;
  color: #6c757d;
  cursor: pointer;
  line-height: 1;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.2s;
}
.close-btn:hover { background: #f8f9fa; color: #2c3e50; }

.panel-body {
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #495057;
}

.form-label.required::after {
  content: ' *';
  color: #e74c3c;
}

.form-input {
  padding: 0.6rem 0.9rem;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #2c3e50;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.15);
}

.form-textarea { resize: vertical; min-height: 80px; }

/* Actions */
.form-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background: #f8f9fa;
  color: #495057;
  border: 1.5px solid #dee2e6;
}
.btn-secondary:hover { background: #e9ecef; }

.create-error {
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fdecea;
  color: #b42318;
  font-size: 0.9rem;
}
.btn-primary {
  background: #3498db;
  color: white;
}
.btn-primary:hover:not(:disabled) { background: #2980b9; transform: translateY(-1px); }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

@media (max-width: 480px) {
  .form-row { grid-template-columns: 1fr; }
}
</style>
